"use client";

import React from "react";
import EditSection, { TextEditor } from "../../sections/shared/EditSection";
import { SectionContentProps } from "../../types";
import { Input, Textarea } from "rizzui";
import { PiPlusBold, PiTrash, PiUploadSimple } from "react-icons/pi";
import homepageApi from "@/services/homepageApi";
import toast from "react-hot-toast";

export default function UniversityResearchEdit({
  content,
  onContentChange,
}: SectionContentProps) {
  const updateContent = (key: string, value: any) => {
    onContentChange?.({ ...content, [key]: value });
  };

  const items = content.items || [
    { title: "Neuroscience Research Center", excerpt: "Exploring the complexities of the human brain through interdisciplinary study.", link: "#", image: "" },
    { title: "Clean Energy Technology", excerpt: "Developing sustainable and efficient solar cell materials for the future.", link: "#", image: "" },
  ];

  return (
    <EditSection
      content={content}
      onContentChange={onContentChange || (() => {})}
      contentFields={
        <div className="space-y-5">
          <div>
            <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5 block">
              Section Title
            </label>
            <Input
              value={content.sectionTitle || ""}
              onChange={(e) => updateContent("sectionTitle", e.target.value)}
              placeholder="PUBLICATION & RESEARCH"
              className="!rounded-lg"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5 block">
              Section Description
            </label>
            <Textarea
              value={content.description || ""}
              onChange={(e) => updateContent("description", e.target.value)}
              placeholder="Description of research publication..."
              className="!rounded-lg"
              rows={3}
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5 block">
                Link Text
              </label>
              <Input
                value={content.viewAllText || ""}
                onChange={(e) => updateContent("viewAllText", e.target.value)}
                placeholder="Learn More"
                className="!rounded-lg"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5 block">
                Link URL
              </label>
              <Input
                value={content.viewAllLink || ""}
                onChange={(e) => updateContent("viewAllLink", e.target.value)}
                placeholder="#"
                className="!rounded-lg font-mono"
              />
            </div>
          </div>

          <div className="border-t border-gray-100 pt-4">
            <div className="flex items-center justify-between mb-4">
              <label className="text-xs font-bold text-gray-700 uppercase tracking-wide">
                Research Items
              </label>
              <button
                onClick={() => {
                  updateContent("items", [
                    ...items,
                    { title: "New Research Item", excerpt: "Excerpt description...", link: "#", image: "" }
                  ]);
                }}
                className="text-xs text-blue-600 hover:text-blue-700 font-semibold flex items-center gap-1"
              >
                <PiPlusBold className="w-3 h-3" /> Add Item
              </button>
            </div>

            <div className="space-y-4">
              {items.map((item: any, idx: number) => (
                <div key={idx} className="p-4 bg-gray-50 rounded-xl border border-gray-200 space-y-3 relative group">
                  <button
                    onClick={() => {
                      const updated = items.filter((_: any, i: number) => i !== idx);
                      updateContent("items", updated);
                    }}
                    className="absolute top-2 right-2 p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    title="Remove item"
                  >
                    <PiTrash className="w-4 h-4" />
                  </button>

                  {/* Image Upload Area */}
                  <div>
                    <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wide mb-1 block">
                      Thumbnail Image
                    </label>
                    <div className="flex items-center gap-3">
                      <div className="w-16 h-10 rounded-lg overflow-hidden border border-gray-200 bg-gray-100 flex-shrink-0 relative group/img">
                        {item.image || item.imageUrl ? (
                          <img
                            src={item.image || item.imageUrl}
                            alt=""
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-400">
                            🖼️
                          </div>
                        )}
                        <label className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover/img:opacity-100 transition-opacity cursor-pointer">
                          <PiUploadSimple className="w-4 h-4 text-white" />
                          <input
                            type="file"
                            className="hidden"
                            accept="image/*"
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              if (file.size > 2 * 1024 * 1024) {
                                toast.error("Image must be under 2MB");
                                return;
                              }
                              const uploadPromise = homepageApi.uploadImage(file);
                              toast.promise(uploadPromise, {
                                loading: "Uploading image...",
                                success: "Image uploaded!",
                                error: "Failed to upload",
                              });
                              const response = await uploadPromise;
                              if (response.success && response.data?.url) {
                                const updated = [...items];
                                updated[idx] = { ...item, image: response.data.url };
                                updateContent("items", updated);
                              }
                            }}
                          />
                        </label>
                      </div>
                      <Input
                        value={item.image || item.imageUrl || ""}
                        onChange={(e) => {
                          const updated = [...items];
                          updated[idx] = { ...item, image: e.target.value };
                          updateContent("items", updated);
                        }}
                        placeholder="Or paste image URL"
                        className="!rounded-lg text-xs flex-1 h-8"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wide mb-1 block">
                      Title
                    </label>
                    <Input
                      value={item.title || ""}
                      onChange={(e) => {
                        const updated = [...items];
                        updated[idx] = { ...item, title: e.target.value };
                        updateContent("items", updated);
                      }}
                      placeholder="Research Title"
                      className="!rounded-lg h-8 text-sm font-semibold"
                    />
                  </div>

                  <div>
                    <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wide mb-1 block">
                      Excerpt / Description
                    </label>
                    <Textarea
                      value={item.excerpt || ""}
                      onChange={(e) => {
                        const updated = [...items];
                        updated[idx] = { ...item, excerpt: e.target.value };
                        updateContent("items", updated);
                      }}
                      placeholder="Excerpt details..."
                      className="!rounded-lg text-xs"
                      rows={2}
                    />
                  </div>

                  <div>
                    <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wide mb-1 block">
                      Link URL
                    </label>
                    <Input
                      value={item.link || ""}
                      onChange={(e) => {
                        const updated = [...items];
                        updated[idx] = { ...item, link: e.target.value };
                        updateContent("items", updated);
                      }}
                      placeholder="#"
                      className="!rounded-lg h-8 text-xs font-mono"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      }
    />
  );
}
